import { DBSchema, openDB } from 'idb';

const DB_NAME = 'anag_upc';
const STORE_NAME = 'prescriptionValues';

export const TTL_MS = 72 * 60 * 60 * 1000;
const MAX_RECORDS = 5000;

export type ProductIDB = {
  id: string; // nuova PK
  upc: string;
  rxvalue: string | null;
  prescription: string | null; //"+5/-4"
  store: string;
  salesOrganization: string;
  distributionChannel: string;
  timestamp: number; // Date.now()
};

interface ANAG_UPC_DB extends DBSchema {
  prescriptionValues: {
    key: string; // id
    value: ProductIDB;
    indexes: {
      store_idx: string;
      salesOrg_idx: string;
      distrChannel_idx: string;
      by_timestamp: string;
    };
  };
}

export const dbPromise = openDB<ANAG_UPC_DB>(DB_NAME, 2, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, {
        keyPath: 'id',
      });

      store.createIndex('store_idx', 'store');
      store.createIndex('salesOrg_idx', 'salesOrganization');
      store.createIndex('distrChannel_idx', 'distributionChannel');
      store.createIndex('by_timestamp', 'timestamp');
    }
  },
});

// Insert / Update
export const saveRecord = async (record: ProductIDB): Promise<void> => {
  const db = await dbPromise;

  try {
    await db.put(STORE_NAME, record);
    return;
  } catch (error) {
    if (error?.name === 'QuotaExceededError') {
      console.warn('IndexedDB pieno! Avvio cleanup...');

      await cleanupOldRecords(TTL_MS);
      await enforceMaxRecords();

      // retry una sola volta
      await db.put(STORE_NAME, record);
      return;
    }

    throw error;
  }
};

// Insert / Update multiplo
export const saveRecords = async (records: ProductIDB[]): Promise<void> => {
  const db = await dbPromise;

  const write = async (): Promise<void> => {
    const tx = db.transaction(STORE_NAME, 'readwrite');

    for (const record of records) {
      await tx.store.put(record);
    }

    await tx.done;
  };

  try {
    await write();
  } catch (error) {
    if (error?.name === 'QuotaExceededError') {
      console.warn('IndexedDB pieno! Avvio cleanup...');

      await cleanupOldRecords(TTL_MS);
      await enforceMaxRecords();

      await write(); // retry
      return;
    }

    throw error;
  }
};

// Get by key
export const getRecord = async (
  key: string
): Promise<ProductIDB | undefined> => {
  const db = await dbPromise;
  return db.get(STORE_NAME, key);
};

// Get all
export const getAllRecords = async (): Promise<ProductIDB[]> => {
  const db = await dbPromise;
  return db.getAll(STORE_NAME);
};

// Delete
export const deleteRecord = async (key: string): Promise<void> => {
  const db = await dbPromise;
  return db.delete(STORE_NAME, key);
};

// Delete multipla
export const deleteRecords = async (keys: string[]): Promise<void> => {
  if (keys.length === 0) return;

  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, 'readwrite');

  try {
    for (const key of keys) {
      await tx.store.delete(key);
    }

    await tx.done;
  } catch (err) {
    throw err;
  }
};

// Ottimizzazione sulla validità dei dati
export const cleanupOldRecords = async (maxAgeMs: number): Promise<void> => {
  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.store;

  const now = Date.now();
  let cursor = await store.openCursor();

  while (cursor) {
    const record = cursor.value;
    const recordTimestamp =
      typeof record.timestamp === 'string'
        ? new Date(record.timestamp).getTime()
        : record.timestamp;

    if (now - recordTimestamp > maxAgeMs) {
      await cursor.delete();
    }

    cursor = await cursor.continue();
  }

  await tx.done;
};

// Ottimizzazione strutturale sulle dimensioni del DB
export const enforceMaxRecords = async (): Promise<void> => {
  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.store;

  const count = await store.count();
  if (count <= MAX_RECORDS) {
    await tx.done;
    return;
  }

  let toDelete = count - MAX_RECORDS;
  const index = store.index('by_timestamp');
  let cursor = await index.openCursor();

  while (toDelete > 0 && cursor) {
    await cursor.delete();
    toDelete--;
    cursor = await cursor.continue();
  }

  await tx.done;
};

export const isExpired = (productIDB: ProductIDB): boolean =>
  Date.now() - productIDB.timestamp > TTL_MS;
